import React, { useState, useId } from 'react';
import { ChevronDownIcon, ExternalLinkIcon, FileTextIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
type ResearchStatus = 'verified' | 'illustrative' | 'pending';
type ResearchSourceProps = {
  sourceId: string;
  status: ResearchStatus;
  title: string;
  summary: string;
  methodology: string;
  sourceUrl?: string;
  sourceLabel?: string;
  published?: string;
  className?: string;
};
const statusCopy: Record<ResearchStatus, string> = {
  verified: 'VERIFIED PUBLIC RESEARCH',
  illustrative: 'ILLUSTRATIVE PROTOTYPE',
  pending: 'SOURCE REQUIRED / PENDING VALIDATION'
};
const statusTone: Record<ResearchStatus, string> = {
  verified: 'border-[#72c7e8]/50 bg-[#062c50] text-white',
  illustrative: 'border-[#ff4d17]/40 bg-[#fff4ef] text-[#11110f]',
  pending: 'border-black/20 bg-[#f7f5ef] text-[#11110f]'
};
export function ResearchSource({
  sourceId,
  status,
  title,
  summary,
  methodology,
  sourceUrl,
  sourceLabel = 'OPEN SOURCE',
  published,
  className = ''
}: ResearchSourceProps) {
  const [isOpen, setIsOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const contentId = useId();
  return <aside className={`border ${statusTone[status]} ${className}`} aria-label={`Evidence record ${sourceId}`} data-id="element-722">
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-723">
        <div className="min-w-0" data-id="element-724">
          <div className="flex flex-wrap items-center gap-2 text-[10px] font-extrabold tracking-[0.12em]" data-id="element-725">
            <span className="border border-current/30 px-2 py-1" data-id="element-726">
              {sourceId}
            </span>
            <span className="opacity-75" data-id="element-727">{statusCopy[status]}</span>
          </div>
          <p className="mt-3 text-sm font-extrabold" data-id="element-728">{title}</p>
        </div>
        <button type="button" aria-expanded={isOpen} aria-controls={contentId} onClick={() => setIsOpen(current => !current)} className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 self-start border border-current/30 px-3 text-xs font-extrabold transition-opacity hover:opacity-70 sm:self-auto" data-id="element-729">
          SOURCE DETAILS
          <motion.span animate={reduceMotion ? undefined : {
          rotate: isOpen ? 180 : 0
        }} transition={{
          duration: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-730">
            <ChevronDownIcon size={15} aria-hidden="true" data-id="element-731" />
          </motion.span>
        </button>
      </div>

      <AnimatePresence initial={false} data-id="element-732">
        {isOpen && <motion.div id={contentId} initial={reduceMotion ? false : {
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={reduceMotion ? undefined : {
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.22,
        ease: [0.22, 1, 0.36, 1]
      }} className="border-t border-current/25 px-4 py-4" data-id="element-733">
            <div className="grid gap-4 md:grid-cols-[0.85fr_1.15fr]" data-id="element-734">
              <div data-id="element-735">
                <p className="text-[10px] font-extrabold tracking-[0.12em] opacity-70" data-id="element-736">
                  WHAT THIS RECORD MEANS
                </p>
                <p className="mt-2 text-sm leading-relaxed opacity-90" data-id="element-737">
                  {summary}
                </p>
              </div>
              <div data-id="element-738">
                <p className="text-[10px] font-extrabold tracking-[0.12em] opacity-70" data-id="element-739">
                  METHOD / PROVENANCE
                </p>
                <p className="mt-2 text-sm leading-relaxed opacity-90" data-id="element-740">
                  {methodology}
                </p>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-current/25 pt-4 text-xs font-bold" data-id="element-741">
              {published && <span data-id="element-742">PUBLISHED {published}</span>}
              {sourceUrl ? <a href={sourceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 underline decoration-current/40 underline-offset-4 hover:decoration-current" data-id="element-743">
                  <ExternalLinkIcon size={14} aria-hidden="true" data-id="element-744" />
                  {sourceLabel}
                </a> : <span className="inline-flex items-center gap-1.5 opacity-75" data-id="element-745">
                  <FileTextIcon size={14} aria-hidden="true" data-id="element-746" />
                  NO APPROVED EXTERNAL SOURCE ATTACHED
                </span>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </aside>;
}